import React, { useContext } from 'react'
import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts'
import { useTranslation } from 'react-i18next'
import DataSkeleton from '@/pages/analytics/ZtSubData/dataSkeleton'
import { Button, Skeleton } from 'antd'
import { textCsatMapping } from '../config'
import PopoverItem from '@/pages/analytics/Overview/popoverTooltip'
import { useExport, TiktokContext } from '../request'

echarts.registerTheme('theme_jekka', {
	backgroundColor: '#fff'
})

const ReceptionMode: React.FC = () => {
	const { patternData: data, loading } = useContext(TiktokContext)
	const { t } = useTranslation()

	const { exportLoading, handleExport } = useExport(12)
	const summary: any[] = Array.isArray(data?.list) ? data.list : []

	const chartData = data?.chart?.reduce(
		(acc: any, item: any) => {
			acc.nativeDate.push(item.stat_date)
			acc.date.push(item.stat_date.slice(5).replace('-', '/'))

			acc.aiOnly.push(item.ai_only === '--' ? null : Number(item.ai_only))

			acc.aiAgent.push(item.ai_agent === '--' ? null : Number(item.ai_agent))

			acc.agentOnly.push(item.agent_only === '--' ? null : Number(item.agent_only))

			return acc
		},
		{
			date: [] as string[],
			nativeDate: [] as string[],
			aiOnly: [] as (number | null)[],
			aiAgent: [] as (number | null)[],
			agentOnly: [] as (number | null)[]
		}
	) || {
		date: [],
		nativeDate: [],
		aiOnly: [],
		aiAgent: [],
		agentOnly: []
	}

	const optionList = {
		tooltip: {
			trigger: 'axis',
			confine: true,
			extraCssText: 'max-width: 450px; white-space: normal;',
			formatter: (params: any[]) => {
				if (!params?.length) return ''

				const date = chartData.nativeDate?.[params[0].dataIndex] || params[0].axisValueLabel

				const getValue = (seriesName: string) => {
					const item = params.find(v => v.seriesName === seriesName)
					return item?.value ?? '0'
				}

				return `<div style="margin-bottom:8px;">
                            <div style="margin-bottom:8px;font-weight:bold;">
                            ${t(
															'a_s_satisfaction_title_2'
														)}：<span style="font-weight:normal;">${date}</span>
                            </div>

                            <div style="margin-bottom:6px;display:flex;align-items:center;">
                                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#ffb72b;margin-right:6px;"></span>
                                <span>${t('a_s_pure_AI_reception')}：</span>
                                <span style="font-weight:bold;">
                                    ${getValue(t('a_s_pure_AI_reception'))}
                                </span>
                            </div>

                            <div style="margin-bottom:6px;display:flex;align-items:center;">
                                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#6CE565;margin-right:6px;"></span>
                                <span>${t('a_s_AI_human_satisfaction')}：</span>
                                <span style="font-weight:bold;">
                                    ${getValue(t('a_s_AI_human_satisfaction'))}
                                </span>
                            </div>

                            <div style="display:flex;align-items:center;">
                                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#FE7070;margin-right:6px;"></span>
                                <span>${t('a_s_artificial_satisfaction')}：</span>
                                <span style="font-weight:bold;">
                                    ${getValue(t('a_s_artificial_satisfaction'))}
                                </span>
                            </div>
                        </div>`
			}
		},

		legend: {
			data: [
				t('a_s_pure_AI_reception'),
				t('a_s_AI_human_satisfaction'),
				t('a_s_artificial_satisfaction')
			],
			top: 10
		},
		grid: {
			left: 10,
			right: 10,
			bottom: 12,
			top: 70,
			containLabel: true
		},

		xAxis: {
			type: 'category',
			data: chartData.date
		},
		yAxis: [
			{
				type: 'value',
				position: 'left',
				min: 0,
				max: 5,
				interval: 1
			},
			{
				type: 'value',
				position: 'right',
				axisLabel: {
					formatter: (value: number) => `${Math.round(value)}%`
				},
				splitLine: { show: false }
			}
		],

		series: [
			{
				name: t('a_s_pure_AI_reception'),
				type: 'bar',
				data: chartData?.aiOnly,
				itemStyle: { color: '#ffb72b', barBorderRadius: [8, 8, 0, 0] },
				label: {
					show: false
				},
				yAxisIndex: 0
			},
			{
				name: t('a_s_AI_human_satisfaction'),
				type: 'bar',
				data: chartData?.aiAgent,
				itemStyle: { color: '#6CE565', barBorderRadius: [8, 8, 0, 0] },
				label: {
					show: false
				},
				yAxisIndex: 0
			},
			{
				name: t('a_s_artificial_satisfaction'),
				type: 'bar',
				data: chartData?.agentOnly,
				itemStyle: { color: '#FE7070', barBorderRadius: [8, 8, 0, 0] },
				label: {
					show: false
				},
				yAxisIndex: 0
			}
		]
	}

	const formatScore = (value?: string | number) => (value === '--' || value === null ? 0 : value)

	const scoreList = summary.map(item => ({ value: formatScore(item?.avg_satisfaction_score) }))

	return (
		<div className="bg-white rounded-lg overflow-hidden px-4 w-[49.5%]">
			<div className="mt-[18px]">
				<div className="text-[14px] flex items-center justify-between mb-[12px]">
					<div className="font-medium text-[#000]">{t('a_s_satisfaction_title_2')}</div>
					<Button
						className="px-[20px]"
						type="primary"
						loading={exportLoading}
						onClick={handleExport}
					>
						{t('qr_batch_download')}
					</Button>
				</div>

				<div className="flex w-full text-[#464646] text-[14px]">
					{scoreList.map((item, index) => {
						const config = Object?.values(textCsatMapping.reception_mode)[index]
						return (
							<div key={index} className="flex-1">
								<div className="flex">
									<PopoverItem title={config.title} titleTooltip={config} />
								</div>

								<div className="text-[20px] font-bold text-[#000]">
									{loading ? (
										<Skeleton.Input active size="small" />
									) : (
										<DataSkeleton value={item.value} />
									)}
								</div>
							</div>
						)
					})}
				</div>
			</div>

			<ReactECharts
				theme="theme_jekka"
				option={optionList}
				showLoading={loading}
				style={{ height: 350, width: '100%' }}
			/>
		</div>
	)
}

export default ReceptionMode
